// =======================================================
// Mapa de instalaciones: plano por planta con el estado de cada aula a una hora,
// buscador de aula libre y la línea del día (aulas × horas). Diseño B (oct. 2026).
// El administrador dibuja, mueve, redimensiona, ordena, renombra y borra aulas.
// =======================================================

const InteractiveMapView = {
    currentFloor: 1,
    zones: [], // { id, name, x, y, width, height, floor } en % del contenedor cuadrado del plano
    editing: false, // «Editar plano» abierto (solo administrador)
    editMode: 'draw', // 'draw' (dibujar aulas nuevas) o 'edit' (mover / cambiar tamaño)
    selectedDate: null, // YYYY-MM-DD
    selectedTime: null, // HH:MM a la que se pinta el estado de las aulas
    selectedRoom: null, // nombre del aula en minúsculas
    findFree: false,
    freeFrom: '16:00',
    freeTo: '18:00',
    zoomLevel: 1,

    // Arrastre / cambio de tamaño
    activeAction: null,
    actionZoneId: null,
    dragStartX: 0,
    dragStartY: 0,
    zoneOriginalX: 0,
    zoneOriginalY: 0,
    zoneOriginalW: 0,
    zoneOriginalH: 0,

    // Las coordenadas guardadas se miden sobre un contenedor cuadrado con la imagen
    // «contain»; la posición de la imagen (centro / arriba) tiene que seguir igual.
    FLOORS: [
        { id: 1, label: '1ª planta', image: '/assets/plano_instalaciones.png', position: 'center' },
        { id: 2, label: '2ª planta', image: '/assets/plano_instalaciones_p2.jpg', position: 'top' }
    ],

    _pad(n) { return String(n).padStart(2, '0'); },
    _iso(d) { return `${d.getFullYear()}-${this._pad(d.getMonth() + 1)}-${this._pad(d.getDate())}`; },
    _todayIso() { return this._iso(new Date()); },
    _nowHm() { const d = new Date(); return `${this._pad(d.getHours())}:${this._pad(d.getMinutes())}`; },
    _key(name) { return String(name || '').trim().toLowerCase(); },
    _canEdit() { return AuthManager.isAdmin(); },
    _floor(id) { return this.FLOORS.find(f => f.id === id) || this.FLOORS[0]; },
    _start(c) { return c.start || '00:00'; },
    _end(c) { return c.end || '23:59'; },
    _span(c) { return c.start ? `${c.start}–${c.end || ''}` : 'Todo el día'; },

    init() {
        this.loadZones();
        this.selectedDate = this._todayIso();
        this.selectedTime = this._nowHm();
        this.editing = false;
        this.editMode = 'draw';
        this.selectedRoom = null;
        this.findFree = false;
        // El alumno entra en la planta donde tiene clase hoy.
        if (AuthManager.isStudent()) {
            const mine = new Set(this._dayClasses().map(c => c.room));
            const zone = this.zones.find(z => mine.has(this._key(z.name)));
            if (zone) this.currentFloor = zone.floor || 1;
        }
        this.renderMap();
        this.setupDrawArea();
    },

    _formatDateDisplay(dateStr) {
        if (!dateStr) return '';
        const d = new Date(`${dateStr}T12:00:00`);
        return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
    },

    _setDate(iso) {
        const wasToday = this.selectedDate === this._todayIso();
        this.selectedDate = iso;
        if (iso === this._todayIso()) this.selectedTime = this._nowHm();
        else if (wasToday) this.selectedTime = '09:00';
        const input = document.getElementById('map-date-input');
        if (input) input.value = iso;
        const label = document.getElementById('map-date-display');
        if (label) label.textContent = this._formatDateDisplay(iso);
        this.renderMap();
    },

    changeDate(delta) {
        const [y, m, d] = this.selectedDate.split('-').map(Number);
        this._setDate(this._iso(new Date(y, m - 1, d + delta)));
    },

    onDateInputChange(value) {
        if (value) this._setDate(value);
    },

    goToday() { this._setDate(this._todayIso()); },

    setHour(h) {
        this.selectedTime = `${this._pad(h)}:00`;
        this.renderMap();
    },

    setNow() {
        this.selectedTime = this._nowHm();
        this.renderMap();
    },

    toggleFind() {
        this.findFree = !this.findFree;
        this.renderMap();
    },

    setFreeRange(which, value) {
        if (!/^\d{2}:\d{2}$/.test(value || '')) return;
        if (which === 'from') this.freeFrom = value; else this.freeTo = value;
        this.renderMap();
    },

    selectRoom(key) {
        this.selectedRoom = this.selectedRoom === key ? null : key;
        const zone = this.zones.find(z => this._key(z.name) === key);
        if (this.selectedRoom && zone && (zone.floor || 1) !== this.currentFloor) this.currentFloor = zone.floor || 1;
        this.renderMap();
    },

    loadZones() {
        const saved = FileStorage.getItem('ecpv_floorplan_zones');
        this.zones = [];
        if (!saved) return;
        try {
            const zones = JSON.parse(saved);
            let changed = false;
            zones.forEach(z => { if (z && !z.floor) { z.floor = 1; changed = true; } });
            this.zones = zones.filter(Boolean);
            if (changed && this._canEdit()) this.saveZones();
        } catch (e) {
            console.error('Error loading custom zones:', e);
        }
    },

    saveZones() {
        FileStorage.setItem('ecpv_floorplan_zones', JSON.stringify(this.zones));
    },

    zoomIn() {
        if (this.zoomLevel < 3) {
            this.zoomLevel = Math.min(3, Math.round((this.zoomLevel + 0.25) * 100) / 100);
            this._applyZoom();
        }
    },

    zoomOut() {
        if (this.zoomLevel > 0.5) {
            this.zoomLevel = Math.max(0.5, Math.round((this.zoomLevel - 0.25) * 100) / 100);
            this._applyZoom();
        }
    },

    resetZoom() {
        this.zoomLevel = 1;
        this._applyZoom();
    },

    _applyZoom() {
        const wrapper = document.getElementById('map-zoom-wrapper');
        if (wrapper) wrapper.style.transform = `scale(${this.zoomLevel})`;
        const label = document.getElementById('map-zoom-label');
        if (label) label.textContent = Math.round(this.zoomLevel * 100) + '%';
    },

    toggleEditing() {
        if (!this._canEdit()) return;
        this.editing = !this.editing;
        this.editMode = 'draw';
        this.selectedRoom = null;
        this.renderMap();
    },

    toggleEditMode(mode) {
        this.editMode = mode;
        this.renderMap();
    },

    switchFloor(floor) {
        this.currentFloor = floor;
        this.renderMap();
    },

    // Clases del día elegido con aula. El alumno solo ve las de sus formaciones
    // (y, si a una le falta el aula, la de otra sesión de la misma asignatura ese mes).
    _dayClasses() {
        const day = this.selectedDate;
        const all = DataManager.getAll(DataManager.KEYS.CLASSES) || [];
        const onDay = c => c.startDate && day >= c.startDate && day <= (c.endDate || c.startDate);
        let list = [];
        if (AuthManager.isStudent()) {
            const studentId = AuthManager.getStudentId();
            const student = studentId ? DataManager.getById(DataManager.KEYS.STUDENTS, studentId) : null;
            const formations = new Set(((student && student.formations) || []).map(String));
            const month = day.substring(0, 7);
            list = all.filter(c => formations.has(String(c.formationId)) && onDay(c)).map(c => {
                if (c.classroom) return c;
                const twin = all.find(o => String(o.subjectId) === String(c.subjectId) && o.startDate && o.startDate.startsWith(month) && o.classroom);
                return twin ? { ...c, classroom: twin.classroom } : c;
            });
        } else {
            list = all.filter(onDay);
        }
        return list.filter(c => String(c.classroom || '').trim()).map(c => {
            const subject = typeof SubjectService !== 'undefined' ? SubjectService.getSubject(c.subjectId) : null;
            const formation = typeof FormationService !== 'undefined' ? FormationService.getFormation(c.formationId) : null;
            const professor = typeof ProfessorService !== 'undefined' ? ProfessorService.getProfessor(c.professorId) : null;
            return {
                room: this._key(c.classroom),
                roomName: String(c.classroom).trim(),
                start: c.startTime || '',
                end: c.endTime || '',
                subject: subject ? subject.name : 'Clase',
                formation: formation ? (formation.commercialName || formation.name) : '',
                professor: professor ? `${professor.firstName || ''} ${professor.lastName || ''}`.trim() : ''
            };
        }).sort((a, b) => this._start(a).localeCompare(this._start(b)));
    },

    _roomState(key, classes, hm) {
        const mine = classes.filter(c => c.room === key);
        const cur = mine.find(c => this._start(c) <= hm && hm < this._end(c));
        const next = mine.find(c => this._start(c) > hm);
        return { mine, cur, next, state: cur ? 'now' : next ? 'later' : (mine.length ? 'past' : 'free') };
    },

    _busyBetween(key, classes, from, to) {
        return classes.some(c => c.room === key && this._start(c) < to && from < this._end(c));
    },

    // Todas las aulas: las del plano en su orden y, detrás, las que tienen clase pero no están dibujadas.
    _rooms(classes) {
        const rooms = this.zones.map(z => ({ key: this._key(z.name), name: z.name, floor: z.floor || 1, zone: z }));
        const known = new Set(rooms.map(r => r.key));
        classes.forEach(c => {
            if (!known.has(c.room)) { known.add(c.room); rooms.push({ key: c.room, name: c.roomName, floor: null, zone: null }); }
        });
        return rooms;
    },

    _hourRange(classes) {
        let min = 8, max = 21;
        classes.forEach(c => {
            if (c.start) min = Math.min(min, parseInt(c.start, 10));
            if (c.end) { const [h, m] = c.end.split(':').map(Number); max = Math.max(max, m ? h : h - 1); }
        });
        return { min: Math.max(0, min), max: Math.min(23, max) };
    },

    render() {
        if (!this.selectedDate) this.selectedDate = this._todayIso();
        if (!this.selectedTime) this.selectedTime = this._nowHm();
        return `
            <div class="view-container dnr-proposal im-view" style="padding-top: 1rem;">
                <style ${SafeHTML.styleAttr}>
                    /* .dnr-proposal trae display:flex;gap:1.5rem: aquí la raíz ya separa sus bloques con su propio gap. */
                    .im-view { gap: 0.85rem; }
                    .im-view [hidden] { display: none !important; }
                    .im-top { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; }
                    .im-title { font-family: var(--dnr-display); font-weight: 400; font-size: 2.4rem; letter-spacing: 0.02em; color: var(--dnr-ink); margin: 0 0.4rem 0 0; line-height: 1; }
                    .im-spacer { flex: 1; }
                    .im-date-nav { display: inline-flex; align-items: center; gap: 2px; padding: 3px; background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: 11px; }
                    .im-date-nav button { border: 0; background: transparent; cursor: pointer; min-width: 32px; min-height: 32px; border-radius: 8px; color: var(--dnr-ink-mute); }
                    .im-date-nav button:hover { background: var(--dnr-teal-100); }
                    .im-date-label { font: 700 0.82rem var(--dnr-mono); color: var(--dnr-ink); padding: 0 8px; cursor: pointer; white-space: nowrap; text-transform: capitalize; }
                    .im-seg { display: inline-flex; gap: 3px; padding: 3px; background: var(--dnr-surface-sunken); border: 1px solid var(--dnr-line); border-radius: 11px; }
                    .im-seg button { min-height: 32px; padding: 0 12px; border: 0; border-radius: 8px; background: transparent; color: var(--dnr-ink-mute); font-weight: 700; font-size: 0.82rem; cursor: pointer; }
                    .im-seg button.is-on { background: var(--dnr-surface); color: var(--dnr-teal-900); box-shadow: 0 1px 3px rgba(0,0,0,.12); }
                    .im-btn { min-height: 36px; padding: 0 12px; border: 1px solid var(--dnr-line); border-radius: 10px; background: var(--dnr-surface); color: var(--dnr-ink); font-weight: 700; font-size: 0.82rem; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
                    .im-btn:hover { border-color: var(--dnr-teal-400); }
                    .im-btn.is-primary { background: var(--dnr-teal-600); border-color: var(--dnr-teal-600); color: #fff; }
                    .im-btn.is-danger { color: #8E2A21; }
                    .im-label { font: 700 0.68rem var(--dnr-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--dnr-ink-mute); }
                    .im-card { background: var(--dnr-surface); border: 1px solid var(--dnr-line); border-radius: var(--dnr-r-lg, 16px); box-shadow: var(--dnr-shadow-card); }
                    .im-hourbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; padding: 0.6rem 0.75rem; }
                    .im-hours { display: flex; flex-wrap: wrap; gap: 3px; }
                    .im-hour { min-height: 30px; min-width: 36px; border-radius: 8px; font: 700 0.75rem var(--dnr-mono); cursor: pointer; border: 1px solid var(--dnr-line); background: var(--dnr-surface); color: var(--dnr-ink); }
                    .im-hour:hover { border-color: var(--dnr-teal-400); }
                    .im-hour.is-on { background: var(--dnr-ink); border-color: var(--dnr-ink); color: #fff; }
                    .im-time-now { font: 700 0.85rem var(--dnr-mono); color: var(--dnr-ink); }
                    .im-sep { width: 1px; height: 26px; background: var(--dnr-line); }
                    .im-find.is-on { background: var(--dnr-teal-600); border-color: var(--dnr-teal-600); color: #fff; }
                    .im-range { display: inline-flex; align-items: center; gap: 6px; font-size: 0.88rem; color: var(--dnr-ink); }
                    .im-range input { font: 700 0.8rem var(--dnr-mono); border: 1px solid var(--dnr-line); border-radius: 8px; padding: 4px 6px; background: var(--dnr-surface); color: var(--dnr-ink); }
                    .im-range b { color: var(--dnr-teal-700); }
                    .im-layout { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 0.85rem; align-items: start; }
                    .im-plan-card { overflow: hidden; }
                    .im-plan-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; padding: 0.6rem 0.75rem; border-bottom: 1px solid var(--dnr-line); }
                    .im-zoom-label { font: 700 0.72rem var(--dnr-mono); color: var(--dnr-ink-mute); align-self: center; min-width: 40px; text-align: center; }
                    .im-legend { display: inline-flex; flex-wrap: wrap; gap: 6px; font-size: 0.75rem; font-weight: 700; }
                    .im-legend span { padding: 3px 9px; border-radius: 999px; }
                    .im-editbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; padding: 0.6rem 0.75rem; background: var(--dnr-amber-100); border-bottom: 1px solid #F1D9B5; }
                    .im-editbar b { color: #7A4210; font-size: 0.85rem; }
                    .im-editbar .im-seg { background: #F6E2C4; border-color: #F1D9B5; }
                    .im-editbar .im-seg button.is-on { color: #7A4210; }
                    .im-edit-hint { font-size: 0.78rem; color: #7A4210; }
                    .im-plan-body { padding: 0.85rem; background: #FAFCFB; }
                    .im-plan-note { margin: 0 0 0.6rem; font-size: 0.85rem; color: var(--dnr-ink-mute); text-align: center; }
                    .im-plan-scroll { overflow: auto; width: 100%; }
                    .im-plan-zoom { transform-origin: top center; transition: transform 0.2s ease; }
                    .im-plan-clip { position: relative; width: 100%; max-width: 720px; margin: 0 auto; overflow: hidden; aspect-ratio: 1 / 1; }
                    .im-plan { position: relative; width: 100%; aspect-ratio: 1 / 1; background-size: contain; background-repeat: no-repeat; overflow: hidden; }
                    .im-plan.is-drawing { cursor: crosshair; }
                    .im-zone { position: absolute; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; padding: 2px; border-radius: 8px; border: 2px solid var(--dnr-teal-600); background: rgba(20, 134, 122, 0.10); color: var(--dnr-ink); font-family: inherit; cursor: pointer; user-select: none; overflow: hidden; }
                    .im-zone strong { font-size: 0.78rem; font-weight: 800; line-height: 1.1; pointer-events: none; text-align: center; }
                    .im-zone-badge { font-size: 0.62rem; font-weight: 800; padding: 1px 6px; border-radius: 999px; white-space: nowrap; pointer-events: none; background: var(--dnr-teal-100); color: var(--dnr-teal-700); }
                    .im-zone.is-now { border-color: #B4483C; background: rgba(180, 72, 60, 0.18); }
                    .im-zone.is-now .im-zone-badge { background: #FBE4E1; color: #8E2A21; }
                    .im-zone.is-later { border-color: var(--dnr-amber-500); background: rgba(226, 147, 60, 0.16); }
                    .im-zone.is-later .im-zone-badge { background: var(--dnr-amber-100); color: #6B3A0B; }
                    .im-zone.is-past .im-zone-badge, .im-zone.is-taken .im-zone-badge { background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); }
                    .im-zone.is-taken, .im-zone.is-other { border: 1px dashed var(--dnr-line-strong); background: rgba(243, 246, 244, 0.55); color: var(--dnr-ink-mute); }
                    .im-zone.is-open { border-color: var(--dnr-teal-600); background: rgba(20, 134, 122, 0.24); }
                    .im-zone.is-edit { border: 2px dashed var(--dnr-warning); background: rgba(217, 123, 31, 0.12); cursor: move; }
                    .im-zone.is-sel { outline: 3px solid var(--dnr-ink); outline-offset: 1px; z-index: 1; box-shadow: 0 6px 18px rgba(15, 27, 25, 0.25); }
                    .im-zone:focus-visible { outline: 3px solid var(--dnr-teal-900); outline-offset: 2px; }
                    .im-zone-resize { position: absolute; right: 0; bottom: 0; width: 14px; height: 14px; background: var(--dnr-warning); border-radius: 4px 0 6px 0; cursor: se-resize; }
                    .im-drawing-box { position: absolute; border: 2px dashed var(--dnr-teal-500); background: rgba(28, 160, 147, 0.15); display: none; pointer-events: none; border-radius: 6px; }
                    .im-side { overflow: hidden; }
                    .im-side-head { padding: 0.75rem 1rem; border-bottom: 1px solid var(--dnr-line); }
                    .im-side-head h2 { margin: 2px 0 0; font-family: var(--dnr-display); font-weight: 400; font-size: 1.7rem; letter-spacing: 0.03em; line-height: 1; color: var(--dnr-ink); }
                    .im-side-body { padding: 0.5rem; display: flex; flex-direction: column; gap: 4px; max-height: 560px; overflow-y: auto; }
                    .im-busy { display: flex; flex-direction: column; gap: 2px; text-align: left; padding: 8px 10px; border-radius: 10px; font-size: 0.88rem; color: var(--dnr-ink); cursor: pointer; font-family: inherit; border: 1px solid #F1C9C3; background: #FDF3F1; }
                    .im-busy.is-later { border-color: #EBD3AE; background: #FDF7EE; }
                    .im-busy.is-past { border-color: var(--dnr-line); background: var(--dnr-surface-sunken); }
                    .im-busy.is-sel { border-color: var(--dnr-ink); }
                    .im-busy-top { display: flex; align-items: baseline; gap: 6px; }
                    .im-busy-top b { flex: 1; }
                    .im-busy-time { font: 700 0.75rem var(--dnr-mono); color: #8E2A21; }
                    .im-busy.is-later .im-busy-time { color: #7A4210; }
                    .im-busy.is-past .im-busy-time { color: var(--dnr-ink-mute); }
                    .im-busy-what { font-size: 0.8rem; color: var(--dnr-ink-mute); }
                    .im-chips { display: flex; flex-wrap: wrap; gap: 4px; padding: 0 4px 6px; }
                    .im-chip { min-height: 30px; padding: 0 10px; border: 1px solid #BFE0D8; border-radius: 999px; background: var(--dnr-teal-100); color: var(--dnr-teal-700); font-weight: 700; font-size: 0.8rem; cursor: pointer; font-family: inherit; }
                    .im-chip.is-sel { border-color: var(--dnr-ink); }
                    .im-sub { padding: 8px 6px 2px; }
                    .im-empty { margin: 0; padding: 8px 6px; font-size: 0.85rem; color: var(--dnr-ink-mute); }
                    .im-floor-tag { font-size: 0.68rem; font-weight: 700; padding: 1px 6px; border-radius: 4px; background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); }
                    .im-sel { margin: 0.5rem; padding: 0.7rem 0.8rem; border-radius: 12px; background: var(--dnr-surface-sunken); display: flex; flex-direction: column; gap: 6px; }
                    .im-sel-head { display: flex; align-items: center; gap: 8px; }
                    .im-sel-head b { flex: 1; font-size: 1rem; }
                    .im-sel-close { border: 0; background: transparent; cursor: pointer; color: var(--dnr-ink-mute); min-width: 28px; min-height: 28px; border-radius: 6px; }
                    .im-sel-row { display: flex; gap: 8px; font-size: 0.85rem; }
                    .im-sel-row span:first-child { font: 700 0.75rem var(--dnr-mono); min-width: 88px; padding-top: 1px; }
                    .im-zone-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.6rem; border-radius: 10px; border: 1px solid var(--dnr-line); background: var(--dnr-surface-sunken); user-select: none; }
                    .im-zone-item b { flex: 1; min-width: 0; font-size: 0.88rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                    .im-zone-item .im-btn { min-height: 30px; padding: 0 8px; }
                    .zone-drag-handle { color: var(--dnr-line-strong); cursor: grab; padding: 4px 2px; flex-shrink: 0; touch-action: none; }
                    .im-day { overflow: hidden; }
                    .im-day-head { display: flex; align-items: baseline; gap: 10px; padding: 0.7rem 1rem; border-bottom: 1px solid var(--dnr-line); }
                    .im-day-head .im-label { flex: 1; }
                    .im-day-hint { font-size: 0.8rem; color: var(--dnr-ink-mute); }
                    .im-day-scroll { overflow-x: auto; }
                    .im-day-grid { min-width: 820px; padding: 0.4rem 1rem 0.75rem; }
                    .im-day-row { display: grid; grid-template-columns: 170px minmax(0, 1fr); align-items: center; min-height: 36px; border-top: 1px solid var(--dnr-line); }
                    .im-day-row.is-ticks { border-top: 0; min-height: 22px; align-items: end; }
                    .im-day-name { font-size: 0.82rem; font-weight: 700; padding-right: 8px; display: flex; align-items: center; gap: 6px; color: var(--dnr-ink); background: transparent; border: 0; text-align: left; cursor: pointer; font-family: inherit; min-height: 30px; }
                    .im-day-name.is-sel { color: var(--dnr-teal-700); }
                    .im-day-name small { font-size: 0.68rem; font-weight: 600; color: var(--dnr-ink-faint); }
                    .im-day-track { position: relative; height: 36px; }
                    .im-day-track.is-ticks { height: 22px; }
                    .im-tick { position: absolute; bottom: 2px; font: 700 0.68rem var(--dnr-mono); color: var(--dnr-ink-mute); transform: translateX(-50%); }
                    .im-block { position: absolute; top: 5px; bottom: 5px; box-sizing: border-box; padding: 0 8px; border-radius: 7px; font-size: 0.75rem; font-weight: 700; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; font-family: inherit; border: 1px solid #E6C79C; background: var(--dnr-amber-100); color: #7A4210; }
                    .im-block.is-now { border-color: #B4483C; background: #FBE4E1; color: #8E2A21; }
                    .im-block.is-past { border-color: var(--dnr-line); background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute); }
                    .im-now-line { position: absolute; top: 0; bottom: 0; width: 2px; background: var(--dnr-ink); pointer-events: none; }
                    /* Móvil/tableta: el plano a todo el ancho y la lista debajo. */
                    @media (max-width: 960px) {
                        .im-layout { grid-template-columns: minmax(0, 1fr) !important; }
                        .im-layout > * { min-width: 0; }
                        .im-side-body { max-height: none; }
                    }
                </style>

                <header class="im-top">
                    <h1 class="im-title">Mapa de instalaciones</h1>
                    <div class="im-date-nav">
                        <button type="button" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.changeDate(-1);}, [])} aria-label="Día anterior"><i class="fas fa-chevron-left"></i></button>
                        <span style="position: relative; display: inline-flex; align-items: center;">
                            <span id="map-date-display" class="im-date-label" ${UIEvents.attr("click", function(event,captured){const i=document.getElementById('map-date-input');if(i.showPicker)i.showPicker();else i.click();}, [])}>${this._formatDateDisplay(this.selectedDate)}</span>
                            <input type="date" id="map-date-input" value="${this.selectedDate}" aria-label="Elegir día"
                                ${UIEvents.attr("change", function(event,captured){InteractiveMapView.onDateInputChange(this.value);}, [])}
                                style="position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none;">
                        </span>
                        <button type="button" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.changeDate(1);}, [])} aria-label="Día siguiente"><i class="fas fa-chevron-right"></i></button>
                    </div>
                    <span id="im-today-slot"></span>
                    <div class="im-seg" id="im-floors" role="tablist" aria-label="Planta"></div>
                    <span class="im-spacer"></span>
                    <span id="im-edit-slot"></span>
                </header>

                <section class="im-card im-hourbar" id="im-hourbar"></section>

                <div class="im-layout">
                    <section class="im-card im-plan-card">
                        <div class="im-plan-head">
                            <span class="im-legend" id="im-legend"></span>
                            <span class="im-spacer"></span>
                            <div class="im-seg" aria-label="Zoom">
                                <button type="button" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.zoomOut();}, [])} aria-label="Alejar"><i class="fas fa-search-minus"></i></button>
                                <span id="map-zoom-label" class="im-zoom-label">${Math.round(this.zoomLevel * 100)}%</span>
                                <button type="button" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.zoomIn();}, [])} aria-label="Acercar"><i class="fas fa-search-plus"></i></button>
                                <button type="button" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.resetZoom();}, [])} aria-label="Restablecer zoom"><i class="fas fa-compress-arrows-alt"></i></button>
                            </div>
                        </div>
                        <div id="im-editbar"></div>
                        <div class="im-plan-body">
                            <p class="im-plan-note" id="im-plan-note" hidden></p>
                            <div class="im-plan-scroll">
                                <div id="map-zoom-wrapper" class="im-plan-zoom" style="transform: scale(${this.zoomLevel});">
                                    <div class="im-plan-clip" id="im-plan-clip">
                                        <div id="draw-blueprint-container" class="im-plan">
                                            <div id="blueprint-rooms-overlay" style="position: absolute; inset: 0;"></div>
                                            <div id="drawing-box" class="im-drawing-box"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <aside class="im-card im-side" id="im-side"></aside>
                </div>

                <section class="im-card im-day" id="im-day"></section>
            </div>
        `;
    },

    setupDrawArea() {
        if (!this._canEdit()) return;
        const container = document.getElementById('draw-blueprint-container');
        const box = document.getElementById('drawing-box');
        if (!container || !box) return;

        const onMouseMove = (e) => {
            if (!this.isDrawing) return;
            const rect = container.getBoundingClientRect();
            const currentX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const currentY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
            box.style.left = `${Math.min(this.startX, currentX)}px`;
            box.style.top = `${Math.min(this.startY, currentY)}px`;
            box.style.width = `${Math.abs(this.startX - currentX)}px`;
            box.style.height = `${Math.abs(this.startY - currentY)}px`;
        };

        const onMouseUp = () => {
            if (!this.isDrawing) return;
            this.isDrawing = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            const rect = container.getBoundingClientRect();
            const x = parseFloat(box.style.left), y = parseFloat(box.style.top);
            const width = parseFloat(box.style.width), height = parseFloat(box.style.height);
            box.style.display = 'none';
            if (width < 20 || height < 20) return; // un clic suelto no crea aula
            this.promptNewZoneName((x / rect.width) * 100, (y / rect.height) * 100, (width / rect.width) * 100, (height / rect.height) * 100);
        };

        container.addEventListener('mousedown', (e) => {
            if (e.button !== 0 || !this.editing || this.editMode !== 'draw') return;
            const rect = container.getBoundingClientRect();
            this.isDrawing = true;
            this.startX = e.clientX - rect.left;
            this.startY = e.clientY - rect.top;
            Object.assign(box.style, { left: `${this.startX}px`, top: `${this.startY}px`, width: '0px', height: '0px', display: 'block' });
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        });
    },

    promptNewZoneName(x, y, w, h) {
        SafeHTML.fire({
            title: 'Nueva aula',
            input: 'text',
            inputLabel: 'Nombre del aula (el mismo que en Programación lectiva)',
            showCancelButton: true,
            confirmButtonColor: 'var(--primary-color)',
            confirmButtonText: 'Crear aula',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            inputValidator: (value) => {
                if (!value || !value.trim()) return 'El nombre del aula es obligatorio';
            }
        }).then((result) => {
            if (!result.isConfirmed) return;
            const name = result.value.trim();
            this.zones.push({
                id: 'zone_' + Date.now(),
                name,
                x: Math.round(x * 10) / 10,
                y: Math.round(y * 10) / 10,
                width: Math.round(w * 10) / 10,
                height: Math.round(h * 10) / 10,
                floor: this.currentFloor || 1
            });
            this.saveZones();
            this.renderMap();
            this.syncProgramViewClassrooms();
            Utils.showToast(`Aula «${name}» creada`, 'success');
        });
    },

    syncProgramViewClassrooms() {
        const select = document.getElementById('program-classroom-select');
        if (select) {
            const currentSelected = select.value;
            SafeHTML.set(select, this.zones.map(z => `
                <option value="${Utils.sanitizeHTML(z.name)}" ${currentSelected === z.name ? 'selected' : ''}>${Utils.sanitizeHTML(z.name)}</option>
            `).join(''));
        }
    },

    renderMap() {
        // El día elegido puede estar en un mes que aún no se ha cargado.
        if (this.selectedDate && typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            FileStorage.needClasses(this.selectedDate, this.selectedDate, () => this.renderMap());
        }
        const overlay = document.getElementById('blueprint-rooms-overlay');
        if (!overlay) return;

        const isStudent = AuthManager.isStudent();
        const editing = this.editing && this._canEdit();
        const classes = this._dayClasses();
        const hm = this.selectedTime || this._nowHm();
        const find = this.findFree && !isStudent && !editing;
        const floor = this._floor(this.currentFloor);
        const esc = s => Utils.sanitizeHTML(String(s == null ? '' : s));

        // Botón «Hoy», plantas y «Editar plano»
        const isToday = this.selectedDate === this._todayIso();
        SafeHTML.set(document.getElementById('im-today-slot'), isToday ? '' :
            `<button type="button" class="im-btn" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.goToday();}, [])}>Hoy</button>`);
        SafeHTML.set(document.getElementById('im-floors'), this.FLOORS.map(f => `
            <button type="button" role="tab" class="${f.id === this.currentFloor ? 'is-on' : ''}" aria-selected="${f.id === this.currentFloor}"
                ${UIEvents.attr("click", function(event,captured){InteractiveMapView.switchFloor(Number(captured[0]));}, [f.id])}>${esc(f.label)}</button>`).join(''));
        SafeHTML.set(document.getElementById('im-edit-slot'), !this._canEdit() ? '' : editing
            ? `<button type="button" class="im-btn is-primary" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.toggleEditing();}, [])}>Listo</button>`
            : `<button type="button" class="im-btn" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.toggleEditing();}, [])}><i class="fas fa-pen"></i> Editar plano</button>`);

        // Barra de hora y buscador de aula libre
        const range = this._hourRange(classes);
        const hourbar = document.getElementById('im-hourbar');
        hourbar.hidden = editing;
        const hours = [];
        for (let h = range.min; h <= range.max; h++) hours.push(h);
        const hh = parseInt(hm, 10);
        const freeRooms = this.zones.filter(z => !this._busyBetween(this._key(z.name), classes, this.freeFrom, this.freeTo));
        SafeHTML.set(hourbar, `
            <span class="im-label">Hora</span>
            <span class="im-time-now">${esc(hm)}</span>
            <div class="im-hours">
                ${hours.map(h => `<button type="button" class="im-hour ${h === hh ? 'is-on' : ''}" aria-pressed="${h === hh}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.setHour(Number(captured[0]));}, [h])}>${this._pad(h)}</button>`).join('')}
            </div>
            ${isToday ? `<button type="button" class="im-btn" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.setNow();}, [])}>Ahora</button>` : ''}
            ${isStudent ? '' : `
            <span class="im-sep"></span>
            <button type="button" class="im-btn im-find ${find ? 'is-on' : ''}" aria-pressed="${find}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.toggleFind();}, [])}><i class="fas fa-search"></i> Buscar aula libre</button>
            ${find ? `<span class="im-range">de <input type="time" value="${esc(this.freeFrom)}" aria-label="Desde" ${UIEvents.attr("change", function(event,captured){InteractiveMapView.setFreeRange('from',this.value);}, [])}>
                a <input type="time" value="${esc(this.freeTo)}" aria-label="Hasta" ${UIEvents.attr("change", function(event,captured){InteractiveMapView.setFreeRange('to',this.value);}, [])}>
                · <b>${freeRooms.length} ${freeRooms.length === 1 ? 'libre' : 'libres'}</b></span>` : ''}`}
        `);

        // Leyenda
        SafeHTML.set(document.getElementById('im-legend'), editing ? '' : find
            ? `<span style="background: var(--dnr-teal-100); color: var(--dnr-teal-700);">Libre de ${esc(this.freeFrom)} a ${esc(this.freeTo)}</span><span style="background: var(--dnr-surface-sunken); color: var(--dnr-ink-mute);">Ocupada en ese rato</span>`
            : isStudent
                ? `<span style="background: #FBE4E1; color: #8E2A21;">Tu clase ahora</span><span style="background: var(--dnr-amber-100); color: #6B3A0B;">Tu clase más tarde</span>`
                : `<span style="background: #FBE4E1; color: #8E2A21;">En uso</span><span style="background: var(--dnr-amber-100); color: #6B3A0B;">Clase más tarde</span><span style="background: var(--dnr-teal-100); color: var(--dnr-teal-700);">Libre</span>`);

        // Barra de edición
        SafeHTML.set(document.getElementById('im-editbar'), !editing ? '' : `
            <div class="im-editbar">
                <b>Editando la ${esc(floor.label)}</b>
                <div class="im-seg">
                    <button type="button" class="${this.editMode === 'draw' ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.toggleEditMode('draw');}, [])}><i class="fas fa-plus"></i> Dibujar aula</button>
                    <button type="button" class="${this.editMode === 'edit' ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.toggleEditMode('edit');}, [])}><i class="fas fa-arrows-alt"></i> Mover y cambiar tamaño</button>
                </div>
                <span class="im-edit-hint">${this.editMode === 'draw' ? 'Pulsa y arrastra sobre el plano para dibujar un aula.' : 'Arrastra un aula para moverla; tira de su esquina para cambiar el tamaño.'}</span>
                <span class="im-spacer"></span>
                <button type="button" class="im-btn is-danger" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.clearFloor();}, [])}>Vaciar planta</button>
            </div>`);

        // Plano
        const container = document.getElementById('draw-blueprint-container');
        container.style.backgroundImage = `url('${floor.image}')`;
        container.style.backgroundPosition = floor.position;
        container.classList.toggle('is-drawing', editing && this.editMode === 'draw');
        this._fitPlan(floor);
        const floorZones = this.zones.filter(z => (z.floor || 1) === this.currentFloor);
        const note = document.getElementById('im-plan-note');
        note.hidden = floorZones.length > 0;
        note.textContent = this._canEdit()
            ? (editing ? 'Pulsa y arrastra sobre el plano para dibujar tu primera aula de esta planta.' : 'Aún no hay aulas en esta planta. Pulsa «Editar plano» para dibujarlas.')
            : 'Aún no hay aulas dibujadas en esta planta.';

        SafeHTML.set(overlay, floorZones.map(z => {
            const key = this._key(z.name);
            const st = this._roomState(key, classes, hm);
            let cls, badge;
            if (editing) { cls = 'is-edit'; badge = ''; }
            else if (find) {
                const free = !this._busyBetween(key, classes, this.freeFrom, this.freeTo);
                cls = free ? 'is-open' : 'is-taken'; badge = free ? 'Libre' : 'Ocupada';
            } else if (isStudent) {
                cls = st.mine.length ? `is-${st.state}` : 'is-other';
                badge = st.state === 'now' ? `Tu clase · hasta ${st.cur.end || ''}` : st.state === 'later' ? `A las ${st.next.start}` : st.state === 'past' ? 'Ya terminó' : '';
            } else {
                cls = st.state === 'now' ? 'is-now' : st.state === 'later' ? 'is-later' : '';
                badge = st.state === 'now' ? (st.cur.end ? `Hasta ${st.cur.end}` : 'En uso') : st.state === 'later' ? `A las ${st.next.start}` : 'Libre';
            }
            if (!editing && this.selectedRoom === key) cls += ' is-sel';
            const pos = `left: ${z.x}%; top: ${z.y}%; width: ${z.width}%; height: ${z.height}%;`;
            if (editing) {
                const action = this.editMode === 'edit' ? UIEvents.attr("mousedown", function(event,captured){InteractiveMapView.startDragOrResize(event,String(captured[0]));}, [z.id]) : '';
                return `<div class="im-zone ${cls}" id="map-room-div-${esc(z.id)}" style="${pos}${this.editMode === 'draw' ? ' pointer-events: none;' : ''}" ${action}>
                    <strong>${esc(z.name)}</strong>
                    ${this.editMode === 'edit' ? `<span class="im-zone-resize" ${UIEvents.attr("mousedown", function(event,captured){InteractiveMapView.startResizeOnly(event,String(captured[0]));}, [z.id])}></span>` : ''}
                </div>`;
            }
            return `<button type="button" class="im-zone ${cls}" id="map-room-div-${esc(z.id)}" style="${pos}" aria-pressed="${this.selectedRoom === key}"
                ${UIEvents.attr("click", function(event,captured){InteractiveMapView.selectRoom(String(captured[0]));}, [key])}>
                <strong>${esc(z.name)}</strong>${badge ? `<span class="im-zone-badge">${esc(badge)}</span>` : ''}
            </button>`;
        }).join(''));

        this._renderSide(classes, hm, editing, find, isStudent, esc);
        this._renderDay(classes, hm, editing, isStudent, range, esc);
        if (editing) this.setupSidebarDragSort();
    },

    // Recorta el hueco vacío bajo un plano apaisado colocado arriba (la 2ª planta).
    _fitPlan(floor) {
        const img = new Image();
        img.onload = () => {
            const clip = document.getElementById('im-plan-clip');
            if (!clip || this.currentFloor !== floor.id || !img.naturalWidth) return;
            const ratio = img.naturalHeight / img.naturalWidth;
            clip.style.aspectRatio = floor.position === 'top' && ratio < 1 ? `1 / ${ratio.toFixed(4)}` : '1 / 1';
        };
        img.src = floor.image;
    },

    _floorTag(room) {
        return room.floor ? `<span class="im-floor-tag">P${room.floor}</span>` : '<span class="im-floor-tag" title="No está dibujada en el plano">Sin plano</span>';
    },

    _renderSide(classes, hm, editing, find, isStudent, esc) {
        const side = document.getElementById('im-side');
        if (!side) return;

        if (editing) {
            SafeHTML.set(side, `
                <div class="im-side-head"><span class="im-label">Aulas · ${this.zones.length}</span><h2>Arrastra ≡ para ordenar</h2></div>
                <div class="im-side-body" id="custom-zones-sidebar-list">
                    ${this.zones.length ? this.zones.map(z => `
                        <div class="im-zone-item zone-list-item" data-id="${esc(z.id)}">
                            <span class="zone-drag-handle" title="Arrastra para ordenar" aria-hidden="true">≡</span>
                            <b>${esc(z.name)}</b>
                            <span class="im-floor-tag">P${z.floor || 1}</span>
                            <button type="button" class="im-btn zone-card-btn" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.renameZone(String(captured[0]));}, [z.id])}>Renombrar</button>
                            <button type="button" class="im-btn is-danger zone-card-btn" aria-label="Borrar ${esc(z.name)}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.deleteZone(String(captured[0]));}, [z.id])}><i class="fas fa-trash-alt"></i></button>
                        </div>`).join('') : '<p class="im-empty">Aún no hay aulas. Dibuja la primera sobre el plano.</p>'}
                </div>`);
            return;
        }

        const rooms = this._rooms(classes);
        const pick = key => UIEvents.attr("click", function(event,captured){InteractiveMapView.selectRoom(String(captured[0]));}, [key]);
        let selCard = '';
        const sel = this.selectedRoom && rooms.find(r => r.key === this.selectedRoom);
        if (sel) {
            const st = this._roomState(sel.key, classes, hm);
            selCard = `
                <div class="im-sel">
                    <div class="im-sel-head"><b>${esc(sel.name)}</b>${this._floorTag(sel)}
                        <button type="button" class="im-sel-close" aria-label="Cerrar" ${pick(sel.key)}><i class="fas fa-times"></i></button></div>
                    ${st.mine.length ? st.mine.map(c => `<div class="im-sel-row"><span>${esc(this._span(c))}</span><span><b>${esc(c.subject)}</b>${c.professor ? ` · ${esc(c.professor)}` : ''}${c.formation ? ` · ${esc(c.formation)}` : ''}</span></div>`).join('')
                        : '<p class="im-empty" style="padding: 0;">Este día no hay clases en esta aula.</p>'}
                </div>`;
        }

        if (isStudent) {
            const now = classes.filter(c => this._start(c) <= hm && hm < this._end(c));
            SafeHTML.set(side, `
                <div class="im-side-head"><span class="im-label">${esc(this._formatDateDisplay(this.selectedDate))}</span>
                    <h2>${now.length ? `Ahora: ${esc(now[0].roomName)}` : classes.length ? `${classes.length} ${classes.length === 1 ? 'clase' : 'clases'} este día` : 'Sin clases este día'}</h2></div>
                ${selCard}
                <div class="im-side-body">
                    ${classes.length ? classes.map(c => {
                        const state = this._start(c) <= hm && hm < this._end(c) ? '' : this._start(c) > hm ? 'is-later' : 'is-past';
                        return `<button type="button" class="im-busy ${state} ${this.selectedRoom === c.room ? 'is-sel' : ''}" ${pick(c.room)}>
                            <span class="im-busy-top"><b>${esc(c.roomName)}</b><span class="im-busy-time">${esc(this._span(c))}</span></span>
                            <span class="im-busy-what">${esc(c.subject)}${c.professor ? ` · ${esc(c.professor)}` : ''}</span></button>`;
                    }).join('') : '<p class="im-empty">Este día no tienes clases con aula asignada.</p>'}
                </div>`);
            return;
        }

        let head, body;
        if (find) {
            const free = rooms.filter(r => r.zone && !this._busyBetween(r.key, classes, this.freeFrom, this.freeTo));
            head = `<span class="im-label">De ${esc(this.freeFrom)} a ${esc(this.freeTo)}</span><h2>${free.length} ${free.length === 1 ? 'aula libre' : 'aulas libres'}</h2>`;
            body = free.length
                ? `<div class="im-chips" style="padding-top: 4px;">${free.map(r => `<button type="button" class="im-chip ${this.selectedRoom === r.key ? 'is-sel' : ''}" ${pick(r.key)}>${esc(r.name)} · P${r.floor}</button>`).join('')}</div>`
                : '<p class="im-empty">No hay ningún aula libre en ese rato.</p>';
        } else {
            const busy = rooms.map(r => ({ r, st: this._roomState(r.key, classes, hm) })).filter(x => x.st.cur);
            const idle = rooms.filter(r => r.zone && !busy.some(x => x.r.key === r.key));
            head = `<span class="im-label">A las ${esc(hm)}</span><h2>${busy.length} en uso · ${idle.length} ${idle.length === 1 ? 'libre' : 'libres'}</h2>`;
            body = `
                ${busy.map(({ r, st }) => `<button type="button" class="im-busy ${this.selectedRoom === r.key ? 'is-sel' : ''}" ${pick(r.key)}>
                    <span class="im-busy-top"><b>${esc(r.name)}</b>${this._floorTag(r)}<span class="im-busy-time">${esc(this._span(st.cur))}</span></span>
                    <span class="im-busy-what">${esc(st.cur.subject)}${st.cur.professor ? ` · ${esc(st.cur.professor)}` : ''}${st.cur.formation ? ` · ${esc(st.cur.formation)}` : ''}</span></button>`).join('')}
                ${busy.length ? '' : '<p class="im-empty">A esta hora no hay ninguna clase.</p>'}
                ${idle.length ? `<span class="im-label im-sub">Libres a esta hora</span>
                <div class="im-chips">${idle.map(r => `<button type="button" class="im-chip ${this.selectedRoom === r.key ? 'is-sel' : ''}" ${pick(r.key)}>${esc(r.name)}</button>`).join('')}</div>` : ''}`;
        }
        SafeHTML.set(side, `<div class="im-side-head">${head}</div>${selCard}<div class="im-side-body">${body}</div>`);
    },

    _renderDay(classes, hm, editing, isStudent, range, esc) {
        const day = document.getElementById('im-day');
        if (!day) return;
        day.hidden = editing;
        if (editing) return;
        const rooms = this._rooms(classes).filter(r => !isStudent || classes.some(c => c.room === r.key));
        if (!classes.length) {
            SafeHTML.set(day, `<div class="im-day-head"><span class="im-label">El día por aulas</span></div><p class="im-empty" style="padding: 0.8rem 1rem;">Este día no hay clases con aula asignada.</p>`);
            return;
        }
        const from = range.min, span = range.max + 1 - range.min;
        const pct = t => { const [h, m] = String(t).split(':').map(Number); return Math.max(0, Math.min(100, ((h + (m || 0) / 60) - from) / span * 100)); };
        const ticks = [];
        for (let h = from; h <= range.max + 1; h += 2) ticks.push(h);
        SafeHTML.set(day, `
            <div class="im-day-head"><span class="im-label">${isStudent ? 'Tu día por aulas' : 'El día por aulas'}</span><span class="im-day-hint">Pulsa una clase para ver su aula en el plano</span></div>
            <div class="im-day-scroll"><div class="im-day-grid">
                <div class="im-day-row is-ticks"><span></span><div class="im-day-track is-ticks">${ticks.map(h => `<span class="im-tick" style="left: ${pct(`${h}:00`)}%;">${this._pad(h)}:00</span>`).join('')}</div></div>
                ${rooms.map(r => `
                    <div class="im-day-row">
                        <button type="button" class="im-day-name ${this.selectedRoom === r.key ? 'is-sel' : ''}" ${UIEvents.attr("click", function(event,captured){InteractiveMapView.selectRoom(String(captured[0]));}, [r.key])}>${esc(r.name)} ${r.floor ? `<small>P${r.floor}</small>` : '<small>sin plano</small>'}</button>
                        <div class="im-day-track">
                            ${classes.filter(c => c.room === r.key).map(c => {
                                const s = this._start(c), e = this._end(c);
                                const state = s <= hm && hm < e ? 'is-now' : e <= hm ? 'is-past' : '';
                                const left = pct(c.start || `${from}:00`), right = pct(c.end || `${range.max + 1}:00`);
                                return `<button type="button" class="im-block ${state}" style="left: ${left}%; width: calc(${Math.max(1, right - left)}% - 3px);"
                                    title="${esc(`${this._span(c)} · ${c.subject}${c.professor ? ` · ${c.professor}` : ''}${c.formation ? ` · ${c.formation}` : ''}`)}"
                                    ${UIEvents.attr("click", function(event,captured){InteractiveMapView.selectRoom(String(captured[0]));}, [r.key])}>${esc(c.subject)}${c.formation ? ` · ${esc(c.formation)}` : ''}</button>`;
                            }).join('')}
                            <span class="im-now-line" style="left: ${pct(hm)}%;"></span>
                        </div>
                    </div>`).join('')}
            </div></div>`);
    },

    setupSidebarDragSort() {
        if (!this._canEdit()) return;
        const list = document.getElementById('custom-zones-sidebar-list');
        if (!list) return;
        let placeholder = null;

        const getAfterElement = (y) => {
            const items = [...list.querySelectorAll('.zone-list-item:not(.dragging-item)')];
            return items.reduce((closest, child) => {
                const box = child.getBoundingClientRect();
                const offset = y - box.top - box.height / 2;
                return offset < 0 && offset > closest.offset ? { offset, element: child } : closest;
            }, { offset: Number.NEGATIVE_INFINITY }).element;
        };

        list.querySelectorAll('.zone-drag-handle').forEach(handle => {
            const item = handle.closest('.zone-list-item');
            handle.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const rect = item.getBoundingClientRect();
                item.classList.add('dragging-item');
                item.style.opacity = '0.45';
                placeholder = document.createElement('div');
                placeholder.style.cssText = `height: ${rect.height}px; border: 2px dashed var(--dnr-teal-400); border-radius: 10px;`;
                list.insertBefore(placeholder, item.nextSibling);

                const onMove = (ev) => {
                    const after = getAfterElement(ev.clientY);
                    if (after) list.insertBefore(placeholder, after); else list.appendChild(placeholder);
                };
                const onUp = () => {
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    item.classList.remove('dragging-item');
                    item.style.opacity = '';
                    list.insertBefore(item, placeholder);
                    placeholder.remove();
                    placeholder = null;
                    const newOrder = [...list.querySelectorAll('.zone-list-item')].map(el => el.dataset.id);
                    this.zones = newOrder.map(id => this.zones.find(z => z.id === id)).filter(Boolean);
                    this.saveZones();
                    this.syncProgramViewClassrooms();
                    this.renderMap();
                };
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            });
        });
    },

    _bindMove() {
        this._onMove = this._onMove || this.handleMoveAction.bind(this);
        this._onStop = this._onStop || this.stopMoveAction.bind(this);
        document.addEventListener('mousemove', this._onMove);
        document.addEventListener('mouseup', this._onStop);
    },

    startDragOrResize(e, zoneId) {
        if (!this.editing || this.editMode !== 'edit' || e.button !== 0) return;
        if (e.target.closest('.im-zone-resize')) return;
        e.preventDefault();
        e.stopPropagation();
        const zone = this.zones.find(z => z.id === zoneId);
        if (!zone) return;
        this.activeAction = 'drag';
        this.actionZoneId = zoneId;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.zoneOriginalX = zone.x;
        this.zoneOriginalY = zone.y;
        this._bindMove();
    },

    startResizeOnly(e, zoneId) {
        if (!this.editing || this.editMode !== 'edit' || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const zone = this.zones.find(z => z.id === zoneId);
        if (!zone) return;
        this.activeAction = 'resize';
        this.actionZoneId = zoneId;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.zoneOriginalW = zone.width;
        this.zoneOriginalH = zone.height;
        this._bindMove();
    },

    handleMoveAction(e) {
        if (!this.activeAction || !this.actionZoneId) return;
        const container = document.getElementById('draw-blueprint-container');
        const zone = this.zones.find(z => z.id === this.actionZoneId);
        if (!container || !zone) return;
        const rect = container.getBoundingClientRect();
        const deltaX = ((e.clientX - this.dragStartX) / rect.width) * 100;
        const deltaY = ((e.clientY - this.dragStartY) / rect.height) * 100;

        if (this.activeAction === 'drag') {
            zone.x = Math.round(Math.max(0, Math.min(this.zoneOriginalX + deltaX, 100 - zone.width)) * 10) / 10;
            zone.y = Math.round(Math.max(0, Math.min(this.zoneOriginalY + deltaY, 100 - zone.height)) * 10) / 10;
        } else {
            zone.width = Math.round(Math.max(3, Math.min(this.zoneOriginalW + deltaX, 100 - zone.x)) * 10) / 10;
            zone.height = Math.round(Math.max(3, Math.min(this.zoneOriginalH + deltaY, 100 - zone.y)) * 10) / 10;
        }

        const roomDiv = document.getElementById(`map-room-div-${zone.id}`);
        if (roomDiv) Object.assign(roomDiv.style, { left: `${zone.x}%`, top: `${zone.y}%`, width: `${zone.width}%`, height: `${zone.height}%` });
    },

    stopMoveAction() {
        document.removeEventListener('mousemove', this._onMove);
        document.removeEventListener('mouseup', this._onStop);
        if (!this.activeAction) return;
        this.activeAction = null;
        this.actionZoneId = null;
        this.saveZones();
        this.renderMap();
        this.syncProgramViewClassrooms();
    },

    renameZone(id) {
        const zone = this.zones.find(z => z.id === id);
        if (!zone) return;
        SafeHTML.fire({
            title: 'Renombrar aula',
            input: 'text',
            inputLabel: 'Nuevo nombre',
            inputValue: zone.name,
            showCancelButton: true,
            confirmButtonColor: 'var(--primary-color)',
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            inputValidator: (value) => {
                if (!value || !value.trim()) return 'El nombre no puede estar vacío';
            }
        }).then((result) => {
            if (!result.isConfirmed) return;
            const oldName = zone.name;
            zone.name = result.value.trim();
            this.saveZones();
            this.renderMap();
            this.syncProgramViewClassrooms();
            Utils.showToast(`«${oldName}» ahora se llama «${zone.name}»`, 'success');
        });
    },

    deleteZone(id) {
        const zone = this.zones.find(z => z.id === id);
        if (!zone) return;
        SafeHTML.fire({
            title: '¿Borrar el aula?',
            text: `Se quitará «${zone.name}» del plano. Las clases que la usan no cambian.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#8E2A21',
            confirmButtonText: 'Sí, borrar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then((result) => {
            if (!result.isConfirmed) return;
            this.zones = this.zones.filter(z => z.id !== id);
            this.saveZones();
            this.renderMap();
            this.syncProgramViewClassrooms();
            Utils.showToast('Aula borrada del plano', 'success');
        });
    },

    clearFloor() {
        const floor = this._floor(this.currentFloor);
        const count = this.zones.filter(z => (z.floor || 1) === floor.id).length;
        if (!count) { Utils.showToast(`La ${floor.label} no tiene aulas`, 'info'); return; }
        SafeHTML.fire({
            title: `¿Vaciar la ${floor.label}?`,
            text: `Se quitarán del plano sus ${count} ${count === 1 ? 'aula' : 'aulas'}. Las clases no cambian.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#8E2A21',
            confirmButtonText: 'Sí, vaciar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true
        }).then((result) => {
            if (!result.isConfirmed) return;
            this.zones = this.zones.filter(z => (z.floor || 1) !== floor.id);
            this.saveZones();
            this.renderMap();
            this.syncProgramViewClassrooms();
            Utils.showToast(`${floor.label} vacía`, 'info');
        });
    }
};

window.InteractiveMapView = InteractiveMapView;
